import { FC, useMemo } from "react"
import classNames from "classnames"
import { useSelector } from "react-redux"
import AlertDetailRow from "./AlertLayout/AlertDetailRow"
import AlertInfo from "./AlertLayout/AlertInfo"
import { useAlertData } from "./useAlertData"
import { getLevelIcon, groupLevelsByType } from "./utils"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import WithRoleFeatureToggle from "@/components/WithRoleFeatureToggle"
import { TestId } from "@/constants/globals"
import { PageRoutes } from "@/constants/routes"
import { RootState } from "@/store"
import { selectInmateById } from "@/store/inmatesSlice"
import {
   AlertOxygenLevel,
   AlertThreshold,
   AlertVariant,
   AlertVitals,
   TypefaceVariant,
} from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { getFullInmateInfo } from "@/utils/index"
import "./index.scss"

export enum AlertModalType {
   CELL = "cell",
   WEARABLE = "wearable",
}

export interface AlertModalProps {
   type?: AlertModalType
   open: boolean
   id: string
   alertVariant: keyof typeof AlertVariant
   typefaceColor: keyof typeof TypefaceVariant
   systemConditionType: AlertSystemConditionType | null
   alertName: string
   dateTime: string
   inmateId?: string
   levels: { name: string; type: string }[]
   locationName?: string
   locationType?: string
   // bpm?: number
   // rpm?: number
   vitals: AlertVitals
   heartRateThreshold: AlertThreshold
   respiratoryRateThreshold?: AlertThreshold
   oxygenLevelThreshold?: AlertOxygenLevel
   isCustom: boolean
   onManage: () => void
   onSwap: () => void
   onClose: () => void
   onView: () => void
}

const AlertModal: FC<AlertModalProps> = (props) => {
   const {
      type,
      open,
      alertVariant,
      typefaceColor,
      systemConditionType,
      alertName,
      dateTime,
      // bpm,
      // rpm,
      inmateId,
      levels,
      locationName,
      locationType,
      vitals,
      heartRateThreshold,
      respiratoryRateThreshold,
      oxygenLevelThreshold,
      isCustom,
      onManage,
      onSwap,
      onClose,
      onView,
   } = props

   const {
      informativeTime,
      modalVariantClassName,
      typefaceClassName,
      typefaceIconColor,
      iconName,
      vitalsDisplay,
   } = useAlertData({
      alertVariant,
      typefaceColor,
      systemConditionType,
      dateTime,
      type,
      vitals,
      heartRateThreshold,
      respiratoryRateThreshold,
      oxygenLevelThreshold,
   })

   const groupedLevels = useMemo(
      () => groupLevelsByType(levels ?? []),
      [levels]
   )

   const inmate = useSelector((state: RootState) =>
      selectInmateById(state, inmateId)
   )
   const wearableMacAddress = inmate?.sensor?.macAddress ?? ""
   const inmateDisplayName =
      inmate?.customIdentifier || getFullInmateInfo(inmate)

   const isBrokenWearable = useMemo(
      () => systemConditionType === AlertSystemConditionType.BROKEN_WEARABLE,
      [systemConditionType]
   )

   return (
      <AlertInfo
         open={open}
         className={classNames(
            `alert-modal__informative${modalVariantClassName}`,
            `alert-modal__informative-typeface${typefaceClassName}`
         )}
         isCustom={isCustom}
         headerIconName={iconName}
         headerText={alertName}
         iconColor={typefaceIconColor}
         systemConditionType={systemConditionType}
         informativeTime={informativeTime}
         vitalsDisplay={vitalsDisplay}
         onClose={onClose}
      >
         <>
            <div className="alert-modal__detail-location">
               <div className="alert-modal__detail-location-items">
                  {Object.entries(groupedLevels ?? {})
                     .reverse()
                     .map(([type, names]) => (
                        <AlertDetailRow
                           key={type}
                           iconName={getLevelIcon(type)}
                           value={names}
                           text={type}
                        />
                     ))}
                  {type === AlertModalType.CELL ? (
                     <AlertDetailRow
                        iconName="cell"
                        value={locationName ?? ""}
                        text="Cell"
                     />
                  ) : (
                     <>
                        <AlertDetailRow
                           iconName="location"
                           value={locationName ?? ""}
                           text={locationType ?? ""}
                        />
                        <AlertDetailRow
                           iconName="smartwatch"
                           value={wearableMacAddress}
                           text="ReassureBand"
                        />
                        <AlertDetailRow
                           iconName="inmate"
                           value={inmateDisplayName}
                           text="Inmate"
                        />
                     </>
                  )}
               </div>
            </div>
            <div className="alert-modal__detail-actions">
               {isBrokenWearable && (
                  <Button
                     className="alert-modal__detail-actions-button"
                     iconName="moveInmate"
                     iconPosition={ButtonIconPosition.LEFT}
                     onClick={onSwap}
                     data-testid={TestId.ALERT_MODAL_SWAP_BUTTON}
                  >
                     Swap device
                  </Button>
               )}
               <Button
                  className="alert-modal__detail-actions-button"
                  iconName="checkmark"
                  iconPosition={ButtonIconPosition.LEFT}
                  onClick={onManage}
                  data-testid={TestId.ALERT_MODAL_MANAGE_BUTTON}
               >
                  Manage alarm
               </Button>
               <WithRoleFeatureToggle
                  feature={
                     type === AlertModalType.CELL
                        ? PageRoutes.CELL_DETAIL_OVERVIEW
                        : PageRoutes.INMATE_DETAIL_OVERVIEW
                  }
               >
                  <Button
                     className={classNames(
                        "alert-modal__detail-actions-button",
                        {
                           "alert-modal__detail-actions-button--full-width":
                              isBrokenWearable,
                        }
                     )}
                     iconName="detailView"
                     iconPosition={ButtonIconPosition.LEFT}
                     onClick={onView}
                     data-testid={TestId.ALERT_MODAL_VIEW_BUTTON}
                  >
                     View
                  </Button>
               </WithRoleFeatureToggle>
            </div>
         </>
      </AlertInfo>
   )
}

export default AlertModal
